import React, { useEffect, useState } from 'react';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../../../config/firebase';
import {
  getRegistrationPlaceCode,
  getRegistrationPlaceName,
  isCancelledRegistrationStatus,
} from '../../../utils/registrationPlaceCode';

interface SharedAccommodationRecord {
  id: string;
  firstName: string;
  lastName: string;
  placeCode: string;
  placeName: string;
  startDate: string;
  endDate: string;
  numberOfNights: number;
  status: string;
  familyCount: number;
}

interface AccommodationGroup {
  placeCode: string;
  placeName: string;
  families: SharedAccommodationRecord[];
  totalFamilies: number;
}

const SharedAccommodations: React.FC = () => {
  const [accommodationGroups, setAccommodationGroups] = useState<AccommodationGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchSharedAccommodations = async () => {
      try {
        setLoading(true);
        setError(null);

        const snapshot = await getDocs(collection(db, 'Registrations'));
        const records: SharedAccommodationRecord[] = [];

        snapshot.forEach(docSnap => {
          const data = docSnap.data();
          const placeCode = getRegistrationPlaceCode(data);

          if (placeCode && !isCancelledRegistrationStatus(data.status)) {
            const startDate = data.stayDetails?.startDate || data.selectedDates?.startDate || data.campDates?.arrivalDate || '';
            const endDate = data.stayDetails?.endDate || data.selectedDates?.endDate || data.campDates?.departureDate || '';
            const numberOfNights = data.stayDetails?.numberOfNights || 0;

            const primaryCamperName = data.registrationInfo?.primaryCamperName || '';
            const nameParts = primaryCamperName.split(' ');
            const firstName = nameParts[0] || data.firstName || '';
            const lastName = nameParts.slice(1).join(' ') || data.lastName || '';

            records.push({
              id: docSnap.id,
              firstName,
              lastName,
              placeCode,
              placeName: getRegistrationPlaceName(data),
              startDate,
              endDate,
              numberOfNights,
              status: data.status || 'pending',
              familyCount: 0
            });
          }
        });

        const groupedByPlaceCode: { [key: string]: SharedAccommodationRecord[] } = {};

        records.forEach(record => {
          if (!groupedByPlaceCode[record.placeCode]) {
            groupedByPlaceCode[record.placeCode] = [];
          }
          groupedByPlaceCode[record.placeCode].push(record);
        });

        const groups: AccommodationGroup[] = Object.entries(groupedByPlaceCode)
          .filter(([_, families]) => families.length > 1)
          .map(([placeCode, families]) => {
            const sortedFamilies = families.sort((a, b) => {
              if (!a.startDate && !b.startDate) return 0;
              if (!a.startDate) return 1;
              if (!b.startDate) return -1;
              return new Date(a.startDate).getTime() - new Date(b.startDate).getTime();
            });

            const familiesWithCount = sortedFamilies.map(family => ({
              ...family,
              familyCount: families.length
            }));

            return {
              placeCode,
              placeName: families[0].placeName,
              families: familiesWithCount,
              totalFamilies: families.length
            };
          })
          .sort((a, b) => a.placeCode.localeCompare(b.placeCode));

        setAccommodationGroups(groups);
      } catch (err) {
        console.error('Error fetching shared accommodations:', err);
        setError('Failed to load shared accommodations data');
      } finally {
        setLoading(false);
      }
    };

    fetchSharedAccommodations();
  }, []);

  const _formatDate = (dateString: string) => {
    if (!dateString) return 'Not set';
    try {
      return new Date(dateString).toLocaleDateString('en-CA', {
        weekday: 'short',
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch {
      return 'Invalid date';
    }
  };

  const getStatusClass = (status: string) => {
    return `status-badge ${status.toLowerCase()}`;
  };

  if (loading) return <div>Loading shared accommodations...</div>;
  if (error) return <div className="error">{error}</div>;

  return (
    <div className="shared-accommodations-report">
      <h2>Shared Accommodations</h2>
      <p className="report-description">
        This report shows all accommodations that have multiple families registered.
        Families are sorted by their start date within each accommodation.
      </p>

      {accommodationGroups.length === 0 ? (
        <div className="no-data">No shared accommodations found.</div>
      ) : (
        <div className="accommodation-groups">
          {accommodationGroups.map(group => (
            <div key={group.placeCode} className="accommodation-group">
              <div className="group-header">
                <h3>
                  {group.placeName} ({group.placeCode})
                  <span className="family-count"> - {group.totalFamilies} families</span>
                </h3>
              </div>

              <table className="stats-table">
                <thead>
                  <tr>
                    <th>Primary Camper</th>
                    <th>Status</th>
                    <th>Total Families in Accommodation</th>
                  </tr>
                </thead>
                <tbody>
                  {group.families.map(family => (
                    <tr key={family.id}>
                      <td>{family.firstName} {family.lastName}</td>
                      <td>
                        <span className={getStatusClass(family.status)}>
                          {family.status}
                        </span>
                      </td>
                      <td>{family.familyCount}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ))}
        </div>
      )}

      <div className="report-summary">
        <h3>Summary</h3>
        <p>Total shared accommodations: {accommodationGroups.length}</p>
        <p>Total families in shared accommodations: {accommodationGroups.reduce((sum, group) => sum + group.totalFamilies, 0)}</p>
      </div>
    </div>
  );
};

export default SharedAccommodations;
